import moment from 'moment';
import { summarizePatientAppointments } from './patientHistory';

const identity = item => item;
const randomIdMaffle = () => Math.floor(Math.random() * 1001);

// Keep the display model and locale-sensitive formatting owned by the browser.
function normalizePatient(patient, documentId, studios, states, history, getRandomIdMaffle = randomIdMaffle) {
    const appointments = summarizePatientAppointments(history, undefined, identity);
    return {
        id: documentId,
        id_maffle: patient.id_maffle !== null ? patient.id_maffle : getRandomIdMaffle(),
        nome: patient.nome,
        cognome: patient.cognome,
        data_di_nascita: patient.data_di_nascita == null ? 'N.D' : moment(patient.data_di_nascita).format('DD-MM-YYYY'),
        citta: patient.citta == null ? 'N.D' : patient.citta,
        citta_di_residenza: patient.citta_di_residenza == null ? 'N.D' : patient.citta_di_residenza,
        tipo_provenienza: patient.tipo_provenienza,
        nome_provenienza: patient.nome_provenienza,
        provenienza: patient.tipo_provenienza + ' ' + patient.nome_provenienza,
        cellulare: patient.cellulare,
        studio_di_riferimento: studios,
        ultimo_appuntamento: appointments.prossimoAppuntamento,
        ultimo_appuntamento_effettuato: appointments.ultimoAppuntamento,
        ultimo_appuntamento_mancato: appointments.ultimoAppuntamentoMancato,
        stato: states == null ? [] : states.map(({ documentId, stato, tipo, sede, anno_di_vendita }) => ({
            id: documentId,
            tipo_paziente: tipo,
            stato_paziente: stato?.split(',') ? stato.split(',').sort() : [],
            anno_di_vendita: anno_di_vendita?.split(',') && anno_di_vendita !== ''
                ? anno_di_vendita.split(',').map(Number).sort((a, b) => a - b) : [],
            sede,
        })),
    };
}

export function normalizePatientListResponse(response) {
    if (!Array.isArray(response?.data)) throw new TypeError('Invalid patient list response');
    const randomDraws = response.meta?.idMaffleRandomDraws ?? 0;
    if (!Number.isSafeInteger(randomDraws) || randomDraws < 0) throw new TypeError('Invalid patient list random draw count');
    const randomIds = new Map();
    for (const patient of response.data) {
        if (patient.id_maffle !== null) continue;
        const index = patient.idMaffleRandomIndex;
        if (!Number.isSafeInteger(index) || index < 0 || index >= randomDraws) {
            throw new TypeError('Invalid patient list random draw index');
        }
        randomIds.set(index, undefined);
    }
    // The old mapper draws once per null-code occurrence before deduplication.
    // Preserve that sequence even though the server now sends only unique rows.
    for (let index = 0; index < randomDraws; index++) {
        const value = randomIdMaffle();
        if (randomIds.has(index)) randomIds.set(index, value);
    }
    return response.data.map(patient => {
        const studios = [...(patient.studio_di_riferimento || [])];
        for (const index of patient.missingStudioNameIndices || []) studios[index] = undefined;
        return normalizePatient(
            patient, patient.documentId, studios, patient.state_type_patients, patient.sales_patients,
            () => randomIds.get(patient.idMaffleRandomIndex),
        );
    });
}

// A compact snapshot retains future appointments relative to asOf. If the client
// clock moves behind that boundary, the full relation is needed again.
export function patientListNeedsFullHistory(response, requestedAsOf) {
    const asOf = Date.parse(response?.meta?.asOf);
    if (!Number.isFinite(asOf)) throw new TypeError('Invalid patient list snapshot time');
    const now = Date.now();
    return now < asOf || now < Date.parse(requestedAsOf);
}

export function buildLegacyPatientListUrl(bindingRoleIds) {
    const params = new URLSearchParams();
    params.append('fields[0]', 'name');
    bindingRoleIds.forEach(id => params.append('filters[binding_roles][id]', id));
    ['nome', 'cognome', 'data_di_nascita', 'citta', 'citta_di_residenza', 'tipo_provenienza',
        'nome_provenienza', 'cellulare', 'id_maffle'].forEach((field, index) => {
        params.append(`populate[patients][fields][${index}]`, field);
    });
    for (const [relation, fields] of Object.entries({
        sales_patients: ['data', 'stato', 'tipologia'],
        state_type_patients: ['stato', 'tipo', 'sede', 'anno_di_vendita'],
    })) {
        fields.forEach((field, index) => params.append(`populate[patients][populate][${relation}][fields][${index}]`, field));
    }
    return `api/stores?${params}`;
}

// Used only after a backwards clock adjustment. The slice keeps its original
// store deduplication, including first-occurrence order and discarded duplicates.
export function normalizeLegacyPatientListEntries(response) {
    const entries = [];
    for (const store of response.data) {
        for (const patient of store.attributes.patients.data) {
            const attributes = patient.attributes;
            entries.push(normalizePatient(
                attributes, patient.documentId, store.attributes.name,
                attributes.state_type_patients?.data?.map(({ documentId, attributes }) => ({ documentId, ...attributes })),
                attributes.sales_patients?.data?.map(item => item.attributes),
            ));
        }
    }
    return entries;
}
